import React, { lazy, Suspense } from "react";
import "./App.scss";
import NoMatch from "./Pages/NoMatch";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import SuperAdminLogin from "./Components/SuperAdmin/SuperAdminLogin";
import ResetPassword from "./Components/Common/ResetPassword";
import CreatePassword from "./Components/Common/CreatePassword";
import ProtectedRoute from "./ProtectedRoute";
import SessionExpired from "./Components/Common/SessionExpired";
import { LoadingSpinner } from "./Components/LoadingSpinner";
import AppErrorBoundary from "./Components/Common/AppErrorBoundary";

import Profile from "./Components/Profile";

const Verify = lazy(() => import("./Components/Verify"));

const SuperAdminLayout = lazy(() => import("./Pages/SuperAdminLayout"));
const CompanyAdminLayout = lazy(() => import("./Pages/CompanyAdminLayout"));
const EmployeeLayout = lazy(() => import("./Pages/EmployeeLayout"));
const SuperAdminDashboard = lazy(() =>
  import("./Components/SuperAdmin/Dashboard")
);
const CompanyAdminDashboard = lazy(() =>
  import("./Components/CompanyAdminUser/Dashboard")
);
const EmployeeLaunchPage = lazy(() =>
  import("./Components/Employee/LaunchPage")
);
const CompanySetUpForm = lazy(() =>
  import("./Components/SuperAdmin/CompanySetUp")
);
const UserSetUpForm = lazy(() => import("./Components/SuperAdmin/UserSetUp"));
const EmployeeAuditLogs = lazy(() =>
  import("./Components/CompanyAdminUser/AuditLogs")
);
const VerificationTypes = lazy(() =>
  import("./Components/CompanyAdminUser/VerificationTypes")
);
const InterviewRecord = lazy(() =>
  import("./Components/CompanyAdminUser/InterviewRecord")
);
const RemoteInterviews = lazy(() =>
  import("./Components/CompanyAdminUser/RemoteInterviews")
);
const RemoteAvailability = lazy(() =>
  import("./Components/CompanyAdminUser/RemoteAvailability")
);
const I9Audit = lazy(() => import("./Components/CompanyAdminUser/I9Audit"));
const I9AuditRunDetail = lazy(() =>
  import("./Components/CompanyAdminUser/I9Audit/RunDetail")
);
const I9Form = lazy(() => import("./Components/I9Form"));
const InitiateI9Form = lazy(() => import("./Components/I9Form/InitiateI9Form"));
const FillI9Form = lazy(() => import("./Components/I9Form/FillI9Form"));
const I9FormCompleted = lazy(() => import("./Components/I9Form/I9Success"));
const Everify = lazy(() => import("./Components/I9Form/Everify"));
const EmployeeI9 = lazy(() => import("./Components/Employee/I9Form"));
const EmployeeI9Form = lazy(() =>
  import("./Components/Employee/I9Form/I9ManualForm")
);
const SelectDocumentForBC = lazy(() =>
  import("./Components/Employee/SelectDocumentForBC")
);
const InterviewLobby = lazy(() =>
  import("./Components/Employee/InterviewLobby")
);
const InitiateI9WithBC = lazy(() =>
  import("./Components/Employee/InitiateI9WithBC")
);
const TentativeNonConfirmation = lazy(() =>
  import("./Pages/StatusPages/TentativeNonConfirmation")
);
const ConfirmDetailsPage = lazy(() =>
  import("./Pages/StatusPages/ConfirmDetailsPage")
);
const DocumentPhotoVerification = lazy(() =>
  import("./Pages/StatusPages/DocumentPhotoVerification")
);
const PhotoMatchPage = lazy(() =>
  import("./Pages/StatusPages/PhotoMatchPage")
);
const ScanAndUploadPage = lazy(() =>
  import("./Pages/StatusPages/ScanAndUploadPage")
);
const ManualReviewPage = lazy(() =>
  import("./Pages/StatusPages/ManualReviewPage")
);
const PendingReferralPage = lazy(() =>
  import("./Pages/StatusPages/PendingReferralPage")
);
const ReferredPage = lazy(() =>
  import("./Pages/StatusPages/ReferredPage")
);
const QueuedPage = lazy(() =>
  import("./Pages/StatusPages/QueuedPage")
);
const FinalNcPage = lazy(() =>
  import("./Pages/StatusPages/FinalNcPage")
);
const TechnicalIssuePage = lazy(() =>
  import("./Pages/StatusPages/TechnicalIssuePage")
);

const AppRoutes = () => {
  const router = createBrowserRouter([
    {
      path: "/",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <SuperAdminLogin />
        </Suspense>
      ),
    },
    {
      path: "/login",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <SuperAdminLogin />
        </Suspense>
      ),
    },
    {
      path: "*",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <NoMatch />
        </Suspense>
      ),
    },
    {
      path: "/resetpassword/:token",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <ResetPassword />
        </Suspense>
      ),
    },
    {
      path: "/setpassword/:token",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <CreatePassword />
        </Suspense>
      ),
    },
    {
      path: "/sessionexpired",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <SessionExpired />
        </Suspense>
      ),
    },
    {
      path: "/superadmin",
      element: <ProtectedRoute Module={SuperAdminLayout} allowedRoles={[0]} enforceAccess />,
      children: [
        {
          path: "/superadmin",
          element: <ProtectedRoute Module={SuperAdminDashboard} allowedRoles={[0]} enforceAccess />,
        },
        {
          path: "/superadmin/companysetup",
          element: <ProtectedRoute Module={CompanySetUpForm} allowedRoles={[0]} enforceAccess />,
        },
        {
          path: "/superadmin/company/:companyId",
          element: <ProtectedRoute Module={CompanySetUpForm} allowedRoles={[0]} enforceAccess />,
        },
        {
          path: "/superadmin/usersetup",
          element: <ProtectedRoute Module={UserSetUpForm} allowedRoles={[0]} enforceAccess />,
        },
        {
          path: "/superadmin/usersetup/:userId",
          element: <ProtectedRoute Module={UserSetUpForm} allowedRoles={[0]} enforceAccess />,
        },
      ],
    },
    {
      path: "/companyadmin",
      element: <ProtectedRoute Module={CompanyAdminLayout} allowedRoles={[0, 1, 2]} enforceAccess />,
      children: [
        {
          path: "/companyadmin",
          element: <ProtectedRoute Module={CompanyAdminDashboard} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/usersetup",
          element: <ProtectedRoute Module={UserSetUpForm} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/I9form/:transactionrecordid",
          element: <ProtectedRoute Module={I9Form} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/audit/:empId",
          element: <ProtectedRoute Module={EmployeeAuditLogs} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/i9audit",
          element: <ProtectedRoute Module={I9Audit} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/i9audit/:auditNumber",
          element: <ProtectedRoute Module={I9AuditRunDetail} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/selectverification",
          element: <ProtectedRoute Module={VerificationTypes} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/remoteinterviews",
          element: <ProtectedRoute Module={RemoteInterviews} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/interview/:transactionId",
          element: <ProtectedRoute Module={InterviewRecord} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/remoteavailability",
          element: <ProtectedRoute Module={RemoteAvailability} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/initiateI9",
          element: <ProtectedRoute Module={InitiateI9Form} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/fillI9form",
          element: <ProtectedRoute Module={FillI9Form} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/I9form",
          element: <ProtectedRoute Module={I9Form} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/I9form/documents",
          element: <ProtectedRoute Module={I9Form} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/I9completed",
          element: <ProtectedRoute Module={I9FormCompleted} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
        {
          path: "/companyadmin/everify",
          element: <ProtectedRoute Module={Everify} allowedRoles={[0, 1, 2]} enforceAccess />,
        },
      ],
    },
    {
      path: "/employee",
      element: <ProtectedRoute Module={EmployeeLayout} allowedRoles={[3]} enforceAccess />,
      children: [
        {
          path: "/employee/launch",
          element: <ProtectedRoute Module={EmployeeLaunchPage} allowedRoles={[3]} enforceAccess />,
        },
        {
          path: "/employee/initiateI9",
          element: <ProtectedRoute Module={EmployeeI9} allowedRoles={[3]} enforceAccess />,
        },
        {
          path: "/employee/I9form",
          element: <ProtectedRoute Module={EmployeeI9Form} allowedRoles={[3]} enforceAccess />,
        },
        {
          path: "/employee/I9form/schedule",
          element: <ProtectedRoute Module={EmployeeI9Form} allowedRoles={[3]} enforceAccess />,
        },
        {
          path: "/employee/I9form/schedule/completed",
          element: <ProtectedRoute Module={EmployeeI9Form} allowedRoles={[3]} enforceAccess />,
        },
        {
          path: "/employee/interview/:publicId",
          element: <ProtectedRoute Module={InterviewLobby} allowedRoles={[3]} enforceAccess />,
        },
        {
          path: "/employee/initiateI9/selectDocumentBC",
          element: <ProtectedRoute Module={SelectDocumentForBC} allowedRoles={[3]} enforceAccess />,
        },
        {
          path: "/employee/initiateI9withBC",
          element: <ProtectedRoute Module={InitiateI9WithBC} allowedRoles={[3]} enforceAccess />,
        },
      ],
    },
    {
      path: "/profile",
      element: (
        <Suspense fallback={<LoadingSpinner />}>
          <Profile />
        </Suspense>
      ),
    },
    {
      path: "/verify/:publicId",
      element: <ProtectedRoute Module={Verify} allowedRoles={[0, 1, 2, 3]} enforceAccess />,
    },
    {
      path: "/companyadmin/verify/:publicId",
      element: <ProtectedRoute Module={Verify} allowedRoles={[0, 1, 2, 3]} enforceAccess />,
    },
    {
      path: "/employee/verify/:publicId",
      element: <ProtectedRoute Module={Verify} allowedRoles={[0, 1, 2, 3]} enforceAccess />,
    },
    {
      path: "/case/tentative-nc",
      element: <ProtectedRoute Module={TentativeNonConfirmation} allowedRoles={[0, 1, 2]} enforceAccess />,
    },
    {
      path: "/case/confirm-details",
      element: <ProtectedRoute Module={ConfirmDetailsPage} allowedRoles={[0, 1, 2]} enforceAccess />,
    },
    {
      path: "/case/photo-verification",
      element: <ProtectedRoute Module={DocumentPhotoVerification} allowedRoles={[0, 1, 2]} enforceAccess />,
    },
    {
      path: "/case/photo-match",
      element: <ProtectedRoute Module={PhotoMatchPage} allowedRoles={[0, 1, 2]} enforceAccess />,
    },
    {
      path: "/case/scan-upload",
      element: <ProtectedRoute Module={ScanAndUploadPage} allowedRoles={[0, 1, 2]} enforceAccess />,
    },
    {
      path: "/case/manual-review",
      element: <ProtectedRoute Module={ManualReviewPage} allowedRoles={[0, 1, 2]} enforceAccess />,
    },
    {
      path: "/case/pending-referral",
      element: <ProtectedRoute Module={PendingReferralPage} allowedRoles={[0, 1, 2]} enforceAccess />,
    },
    {
      path: "/case/referred",
      element: <ProtectedRoute Module={ReferredPage} allowedRoles={[0, 1, 2]} enforceAccess />,
    },
    {
      path: "/case/queued",
      element: <ProtectedRoute Module={QueuedPage} allowedRoles={[0, 1, 2]} enforceAccess />,
    },
    {
      path: "/case/final-nc",
      element: <ProtectedRoute Module={FinalNcPage} allowedRoles={[0, 1, 2]} enforceAccess />,
    },
    {
      path: "/case/technical-issue",
      element: <ProtectedRoute Module={TechnicalIssuePage} allowedRoles={[0, 1, 2]} enforceAccess />,
    },
  ]);

  return (
    <AppErrorBoundary>
      <RouterProvider router={router} />
    </AppErrorBoundary>
  );
};

export default AppRoutes;
